import React from 'react';
import classnames from 'classnames';
import { uid } from 'uid';
import ToastCollectionLegacyContent from './ToastCollectionLegacyContent';

const activeToastIds = new Map();
const pendingToasts = [];
let collectionRef = null;

export function getLegacyToastClassName({ className, poorNetworkConnection }) {
  return classnames(className, {
    'poor-net-toast-message': poorNetworkConnection,
  });
}

export function normalizeLegacyToast(message) {
  const { children = null, className, content, icon, iconId, id: messageId, poorNetworkConnection, title, ...toastConfig } = message ?? {};

  return {
    ...toastConfig,
    className: getLegacyToastClassName({ className, poorNetworkConnection }),
    hideCloseLabel: true,
    id: messageId,
    message: React.createElement(
      ToastCollectionLegacyContent,
      {
        content,
        icon,
        iconId,
        title,
      },
      children
    ),
    // eslint-disable-next-line no-use-before-define
    onClose: () => removeToast(messageId),
  };
}

function removePendingToast(messageId) {
  const pendingIndex = pendingToasts.findIndex((message) => message?.id === messageId);

  if (pendingIndex !== -1) {
    pendingToasts.splice(pendingIndex, 1);
  }
}

function addToast(message) {
  if (!collectionRef || !message?.id) {
    return null;
  }

  const activeToast = activeToastIds.get(message.id);

  if (activeToast?.toastId && activeToast.message === message) {
    return activeToast.toastId;
  }

  if (activeToast?.toastId) {
    collectionRef.removeToast(activeToast.toastId);
  }

  const toastId = collectionRef.addToast(normalizeLegacyToast(message));

  activeToastIds.set(message.id, { message, toastId });

  return toastId;
}

function flushPendingToasts() {
  const queuedToasts = pendingToasts.splice(0, pendingToasts.length);

  queuedToasts.forEach((message) => {
    if (activeToastIds.has(message?.id)) {
      addToast(message);
    }
  });
}

export function showToast(message) {
  const toastMessage = {
    ...message,
    id: uid(),
  };

  activeToastIds.set(toastMessage.id, { message: toastMessage, toastId: null });

  if (!collectionRef) {
    pendingToasts.push(toastMessage);
    return toastMessage.id;
  }

  addToast(toastMessage);

  return toastMessage.id;
}

export function removeToast(messageId) {
  if (!messageId) {
    return null;
  }

  const activeToast = activeToastIds.get(messageId);

  removePendingToast(messageId);

  if (collectionRef && activeToast?.toastId) {
    collectionRef.removeToast(activeToast.toastId);
  }

  activeToastIds.delete(messageId);

  return messageId;
}

function setCollectionRef(ref) {
  collectionRef = ref;

  if (collectionRef) {
    flushPendingToasts();
  }
}

function reset() {
  pendingToasts.splice(0, pendingToasts.length);
  activeToastIds.clear();
  collectionRef = null;
}

const ToastCollectionService = {
  removeToast,
  reset,
  setCollectionRef,
  showToast,
};

export default ToastCollectionService;
